import React, { useState, useEffect, useRef, useCallback } from "react";
import { useGlobalLoader } from "../../context/GlobalLoaderContext";
import { addJob, deleteJob, updateJobHomeVisibility } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function HrJobAdminPanel({ darkMode }) {
  const { loading: globalLoading } = useGlobalLoader();
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [jobs, setJobs] = useState([]);
  const [jobsOffset, setJobsOffset] = useState(0);
  const [jobsHasMore, setJobsHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // Remove local isLoading, use global loader
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [formData, setFormData] = useState({ title: "", slug: "" });
  const role = localStorage.getItem("role");

  // Initial load (global state)
  useEffect(() => {
    async function fetchInitial() {
      await fetchThreeTableSection("jobs", false, { limit: PAGE_LIMIT, offset: 0 });
    }
    fetchInitial();
    // eslint-disable-next-line
  }, []);

  // Sync jobs from global state
  useEffect(() => {
    setJobs(threeTable.jobs || []);
    setJobsOffset(threeTable.jobs?.length || 0);
    setJobsHasMore((threeTable.jobs?.length || 0) === PAGE_LIMIT);
  }, [threeTable.jobs]);

  // Infinite scroll fetch
  const fetchMoreJobs = useCallback(async () => {
    if (!jobsHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    await fetchThreeTableSection("jobs", false, { limit: PAGE_LIMIT, offset: jobsOffset });
    setIsFetchingMore(false);
  }, [jobsHasMore, isFetchingMore, jobsOffset, fetchThreeTableSection]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!jobsHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreJobs();
    }
  }, [jobsHasMore, isFetchingMore, fetchMoreJobs]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Job submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.slug.trim()) {
      alert("Please enter both job title and job slug.");
      return;
    }
    await addJob(formData);
    setFormData({ title: "", slug: "" });
    await fetchThreeTableSection("jobs", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this job?")) {
      await deleteJob(id);
      await fetchThreeTableSection("jobs", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const handleToggleHomeVisibility = async (job) => {
    await updateJobHomeVisibility(job._id, job.showOnHome === false);
    await fetchThreeTableSection("jobs", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  if (role !== "hr") {
    return <div className="admin-panel-error">❌ Access denied! Only HR can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--emerald${darkMode ? ' dark-mode' : ''}`}> 
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">🧑‍💼 HR Job Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Yahan se HR jobs manage karo. Job add karo, list me dekho, home par visibility toggle karo ya delete karo.</p>
        </div>
        <div className="resource-admin-panel__pill">HR Jobs</div>
      </div>
      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label className="resource-admin-panel__label">Job Title</label>
          <input
            className="resource-admin-panel__input"
            placeholder="Job Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div className="resource-admin-panel__field">
          <label className="resource-admin-panel__label">Job Slug</label>
          <input
            className="resource-admin-panel__input"
            placeholder="Job Slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
        </div>
        <button className="resource-admin-panel__submit" type="submit">✅ Add Job</button>
      </form>
      <div className="resource-admin-panel__section">
        <div className="resource-admin-panel__section-top">
          <div>
            <h3 className="resource-admin-panel__section-title">Saved Jobs</h3>
            <p className="resource-admin-panel__section-meta">{jobs.length} items</p>
          </div>
        </div>
        {globalLoading ? (
          <div className="global-loader"><div className="skeleton-loader" /></div>
        ) : jobs.length === 0 ? (
          <p className="resource-admin-panel__empty">No jobs found</p>
        ) : (
          <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
            {jobs.map((job) => (
              <div key={job._id} className="resource-admin-panel__item">
                <div>
                  <p className="resource-admin-panel__item-title">{job.title}</p>
                  <p className="resource-admin-panel__item-subtitle">Slug: {job.slug}</p>
                  <span style={{ marginLeft: "8px", color: job.showOnHome === false ? "#d32f2f" : "#2e7d32", fontWeight: "bold" }}>
                    {job.showOnHome === false ? "Hidden on Home" : "Visible on Home"}
                  </span>
                </div>
                <div className="resource-admin-panel__actions">
                  <a
                    href={`/mform/${job.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-admin-panel__link"
                  >
                    View Live
                  </a>
                  {/* Only show the 'Show on Home' button if job.showOnHome is false */}
                  {job.showOnHome === false && (
                    <button
                      className="resource-admin-panel__action-btn"
                      onClick={() => handleToggleHomeVisibility(job)}
                    >
                      Show on Home
                    </button>
                  )}
                  {/* Hide the delete button if job is visible on home */}
                  {job.showOnHome === false && (
                    <button
                      className="resource-admin-panel__action-btn resource-admin-panel__action-btn--danger"
                      onClick={() => handleDelete(job._id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
            {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
            {!jobsHasMore && !isFetchingMore && jobs.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more jobs.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default HrJobAdminPanel;
